/**
 * =====================================================================
 *  CONSTANTES Y TAXONOMÍA OFICIAL EXANI-II (CENEVAL) — UNINAV 2027
 * =====================================================================
 * Base validada en el plan PLAN-EXANI-2027.md y skill exani-ii-base-de-datos.
 *
 * Este archivo es la ÚNICA fuente de verdad de la taxonomía del examen:
 *  - Estructura global (168 reactivos, 138 que puntúan, 3 opciones A/B/C)
 *  - Áreas transversales con subáreas/temas y distribución de reactivos
 *  - 16 módulos disciplinares específicos + 2 generales con desglose
 *  - Asignación de módulos por carrera UNINAV 2026 (base) / 2027 (a validar)
 *  - Metadatos de calificación (Índice Ceneval 700–1300)
 *
 * Los export "legacy" (EXANI_AREAS_TRANSVERSALES, EXANI_MODULOS_*, etc.)
 * se mantienen para compatibilidad con la app existente.
 * =====================================================================
 */

// ---------------------------------------------------------------------
// 1. Metadata global del examen
// ---------------------------------------------------------------------
export const EXANI_META = {
  version: '2027-1',
  examen: 'EXANI-II',
  institucion: 'Universidad Naval (UNINAV) — Secretaría de Marina',
  ciclo: '2026-2027',
  nuevoModelo: true, // 3 opciones de respuesta (A, B, C)
  totalReactivos: 168,
  puntuajeReactivos: 138,
  tiempoMinutos: 270, // 4 h 30 min
  opcionesPorReactivo: 3,
  formato: 'Opción múltiple, 3 opciones de respuesta (A, B, C)',
  calculadora: 'Básica permitida',
  extras: '10% de reactivos piloto + 2 de control (no puntúan)',
  modalidades: ['impreso', 'en_linea', 'desde_casa'],
  // Decisión oficial Hermes (2026-10-14): escala ICNE 800–1300, alineada con
  // exaniConstants.js raíz (student-quiz) y CenevalScaleConverter.kt (student-android).
  escalaPuntaje: { min: 800, max: 1300, unidad: 'Índice Ceneval (ICNE)' },
};

// Composición del puntaje global (138 reactivos)
export const COMPOSICION_PUNTAJE = [
  { area: 'Pensamiento matemático', reactivos: 30, tipo: 'transversal' },
  { area: 'Comprensión lectora', reactivos: 30, tipo: 'transversal' },
  { area: 'Redacción indirecta', reactivos: 30, tipo: 'transversal' },
  { area: 'Módulo disciplinar 1', reactivos: 24, tipo: 'modulo' },
  { area: 'Módulo disciplinar 2', reactivos: 24, tipo: 'modulo' },
];

// Referencia de niveles (contexto Ceneval; cada institución aplica su criterio)
export const NIVELES_ICNE = [
  { rango: '800–999', etiqueta: 'Aún no satisfactorio' },
  { rango: '1000–1149', etiqueta: 'Satisfactorio' },
  { rango: '1150–1300', etiqueta: 'Sobresaliente' },
];

// Convierte un porcentaje de aciertos (0-100) al Índice Ceneval (ICNE 800-1300).
// Paridad con CenevalScaleConverter.kt del cliente Android (MIN 800 / MAX 1300).
export function percentageToIcne(percentage) {
  const { min, max } = EXANI_META.escalaPuntaje;
  const pct = Math.max(0, Math.min(100, Number(percentage) || 0));
  return Math.round(min + (pct / 100) * (max - min));
}

// Etiqueta de nivel de desempeño para un ICNE (800-1300).
export function getNivelIcne(icne) {
  const { min, max } = EXANI_META.escalaPuntaje;
  const value = Math.max(min, Math.min(max, Number(icne) || min));
  if (value < 1000) return NIVELES_ICNE[0].etiqueta;
  if (value < 1150) return NIVELES_ICNE[1].etiqueta;
  return NIVELES_ICNE[2].etiqueta;
}

// Reflexión Premedicina: puntaje global = 4 áreas (30+30+30+48)
export const COMPOSICION_PUNTAJE_PREMEDICINA = [
  { area: 'Pensamiento matemático', reactivos: 30, tipo: 'transversal' },
  { area: 'Comprensión lectora', reactivos: 30, tipo: 'transversal' },
  { area: 'Redacción indirecta', reactivos: 30, tipo: 'transversal' },
  { area: 'Premedicina', reactivos: 48, tipo: 'modulo' },
];

// ---------------------------------------------------------------------
// 2. Áreas transversales (legacy, simple)
// ---------------------------------------------------------------------
export const EXANI_AREAS_TRANSVERSALES = [
  {
    id: 'comprension_lectora',
    name: 'Comprensión Lectora',
    reactivos: 30,
    tipo: 'predictiva',
    icon: '📖',
    descripcion: 'Identificación de información, interpretación y evaluación de la forma y el contenido. Sin memorización ni conocimientos externos.'
  },
  {
    id: 'redaccion_indirecta',
    name: 'Redacción Indirecta',
    reactivos: 30,
    tipo: 'predictiva',
    icon: '✍️',
    descripcion: 'El sustentante elige el fragmento correctamente escrito (comunicativa, gramatical/semántica, ortográfica).'
  },
  {
    id: 'pensamiento_matematico',
    name: 'Pensamiento Matemático',
    reactivos: 30,
    tipo: 'predictiva',
    icon: '📐',
    descripcion: 'Comprensión de lo matemático y matematización: razones, porcentaje, exponentes, inecuaciones, sistemas de ecuaciones, funciones, trigonometría, estadística y probabilidad.'
  },
  {
    id: 'ingles_diagnostico',
    name: 'Inglés como Lengua Extranjera',
    reactivos: 30,
    tipo: 'diagnostica',
    icon: '🌐',
    descripcion: 'Nivel B1 del MCER. Comprensión de lectura y redacción indirecta en inglés. NO suma al puntaje global (solo ubica/propedéutico).'
  }
];

// ---------------------------------------------------------------------
// 2b. Áreas transversales DETALLADAS (subáreas → temas → subtemas)
// ---------------------------------------------------------------------
export const PENSAMIENTO_MATEMATICO_DETALLE = {
  id: 'pm',
  nombre: 'Pensamiento matemático',
  reactivos: 30,
  subareas: [
    {
      id: 'pm-comprension',
      nombre: 'Comprensión de lo matemático',
      reactivos: 18,
      temas: [
        { id: 'pm1-1', nombre: 'Conexiones', subtemas: ['Números y su contexto', 'Vínculos entre conceptos'] },
        { id: 'pm1-2', nombre: 'Estimación', subtemas: ['Aproximación de resultados', 'Cálculo mental'] },
        { id: 'pm1-3', nombre: 'Sentido numérico', subtemas: ['Razones y proporciones', 'Porcentaje', 'Leyes de los exponentes'] }
      ]
    },
    {
      id: 'pm-matematizacion',
      nombre: 'Matematización',
      reactivos: 12,
      temas: [
        { id: 'pm2-1', nombre: 'Desarrollo de usos', subtemas: ['Inecuaciones lineales', 'Sistemas de ecuaciones 2x2 y 3x3', 'Polinomios'] },
        { id: 'pm2-2', nombre: 'Lenguaje matemático', subtemas: ['Interpretación gráfica de funciones lineales y cuadráticas', 'Razones trigonométricas', 'Trazados y ejes de simetría'] },
        { id: 'pm2-3', nombre: 'Resignificaciones', subtemas: ['Media aritmética', 'Desviación estándar', 'Deciles/cuartiles/percentiles', 'Frecuencias', 'Espacio muestral', 'Probabilidad clásica', 'Variación lineal tabular'] }
      ]
    }
  ]
};

export const COMPRENSION_LECTORA_DETALLE = {
  id: 'cl',
  nombre: 'Comprensión lectora',
  reactivos: 30,
  subareas: [
    { id: 'cl-estudio', nombre: 'Ámbito de estudio', reactivos: 12, temas: [
      { id: 'cl1-1', nombre: 'Texto argumentativo-periodístico', subtemas: ['Identificar información implícita', 'Evaluar la forma y el contenido'] },
      { id: 'cl1-2', nombre: 'Ensayo académico', subtemas: ['Interpretación global', 'Identificar el propósito del autor'] }
    ]},
    { id: 'cl-literario', nombre: 'Ámbito literario', reactivos: 12, temas: [
      { id: 'cl2-1', nombre: 'Cuento', subtemas: ['Localización de información explícita/implícita'] },
      { id: 'cl2-2', nombre: 'Poema', subtemas: ['Interpretación de recursos literarios'] }
    ]},
    { id: 'cl-social', nombre: 'Ámbito de participación social', reactivos: 6, temas: [
      { id: 'cl3-1', nombre: 'Noticia', subtemas: ['Evaluación de la forma y el contenido'] },
      { id: 'cl3-2', nombre: 'Documento administrativo', subtemas: ['Interpretación y localización de información'] }
    ]}
  ]
};

export const REDACION_INDIRECTA_DETALLE = {
  id: 'ri',
  nombre: 'Redacción indirecta',
  reactivos: 30,
  subareas: [
    { id: 'ri-estudio', nombre: 'En el ámbito de estudio', reactivos: 15 },
    { id: 'ri-social', nombre: 'En el ámbito de participación social', reactivos: 15 }
  ],
  dimensiones: [
    { id: 'comunicativa', nombre: 'Comunicativa', contenidos: ['Registro lingüístico (formal/informal/coloquial)', 'Adecuación al género textual (prólogo, ensayo, reseña, artículo de opinión, crónica, noticia)'] },
    { id: 'gramatical', nombre: 'Gramatical y semántica', contenidos: ['Concordancia nominal y verbal', 'Uso de pronombres (le/les)', 'Cohesión gramatical', 'Cohesión léxico-semántica (sinónimos, terminología)', 'Cohesión textual (conectores: además, sin embargo, por lo tanto; transiciones)'] },
    { id: 'ortografica', nombre: 'Ortográfica', contenidos: ['Grafofonética (b/v, s/c/z)', 'Homófonos y homógrafos', 'Puntuación', 'Acentuación'] }
  ]
};

export const INGLES_DIAGNOSTICO_DETALLE = {
  id: 'in',
  nombre: 'Inglés como lengua extranjera (B1 MCER)',
  reactivos: 30,
  puntua: false,
  subareas: [
    { id: 'in-reading', nombre: 'Reading Comprehension (B1)', reactivos: 15 },
    { id: 'in-writing', nombre: 'Language Use & Indirect Writing (B1)', reactivos: 15 }
  ]
};

// Mapa de áreas transversales detalladas (índice por clave corta)
export const AREAS_TRANSVERSALES_DETALLE = {
  pm: PENSAMIENTO_MATEMATICO_DETALLE,
  cl: COMPRENSION_LECTORA_DETALLE,
  ri: REDACION_INDIRECTA_DETALLE,
  in: INGLES_DIAGNOSTICO_DETALLE,
};

export const DESGLOSE_TRANSVERSALES = (() => {
  const map = {};
  [PENSAMIENTO_MATEMATICO_DETALLE, COMPRENSION_LECTORA_DETALLE, REDACION_INDIRECTA_DETALLE, INGLES_DIAGNOSTICO_DETALLE]
    .forEach((m) => { map[m.id] = m; });
  return map;
})();

// ---------------------------------------------------------------------
// 3. Módulos de conocimientos disciplinares
// ---------------------------------------------------------------------
export const EXANI_MODULOS_ESPECIFICOS = [
  { id: 'administracion', nombre: 'Administración', reactivos: 24, icon: '💼', especifico: true },
  { id: 'aritmetica', nombre: 'Aritmética', reactivos: 24, icon: '🔢', especifico: true },
  { id: 'biologia', nombre: 'Biología', reactivos: 24, icon: '🧬', especifico: true },
  { id: 'calculo', nombre: 'Cálculo diferencial e integral', reactivos: 24, icon: '∫', especifico: true },
  { id: 'ciencias_salud', nombre: 'Ciencias de la Salud', reactivos: 24, icon: '🩺', especifico: true },
  { id: 'derecho', nombre: 'Derecho', reactivos: 24, icon: '⚖️', especifico: true },
  { id: 'economia', nombre: 'Economía', reactivos: 24, icon: '📈', especifico: true },
  { id: 'filosofia', nombre: 'Filosofía', reactivos: 24, icon: '🏛️', especifico: true },
  { id: 'fisica', nombre: 'Física', reactivos: 24, icon: '⚡', especifico: true },
  { id: 'historia', nombre: 'Historia', reactivos: 24, icon: '📜', especifico: true },
  { id: 'literatura', nombre: 'Literatura', reactivos: 24, icon: '📚', especifico: true },
  { id: 'matematicas_financieras', nombre: 'Matemáticas financieras', reactivos: 24, icon: '💰', especifico: true },
  {
    id: 'premedicina',
    nombre: 'Premedicina',
    reactivos: 48,
    icon: '🏥',
    especifico: true,
    noCombinable: true,
    nota: 'Módulo integrado de 48 reactivos. No se combina con otro módulo.'
  },
  { id: 'probabilidad_estadistica', nombre: 'Probabilidad y estadística', reactivos: 24, icon: '📊', especifico: true },
  { id: 'psicologia', nombre: 'Psicología', reactivos: 24, icon: '🧠', especifico: true },
  { id: 'quimica', nombre: 'Química', reactivos: 24, icon: '🧪', especifico: true }
];

export const EXANI_MODULOS_GENERALES = [
  { id: 'ciencias_experimentales', nombre: 'Ciencias experimentales', reactivos: 24, icon: '🔬', general: true },
  { id: 'ciencias_sociales', nombre: 'Ciencias sociales', reactivos: 24, icon: '👥', general: true }
];

// Desglose de contenidos oficiales conocido (subárea -> temas). El resto se marca pendiente.
export const MODULOS_DETALLE = {
  ciencias_salud: {
    id: 'ciencias_salud',
    nombre: 'Ciencias de la Salud',
    reactivos: 24,
    subareas: [
      { id: 'cs-salud', nombre: 'Estudio del proceso salud-enfermedad', reactivos: 11, temas: ['Terminología', 'Triada ecológica', 'Historia natural de la enfermedad', 'Conductas de riesgo', 'Signos vitales'] },
      { id: 'cs-morfo', nombre: 'Morfofisiología del cuerpo humano', reactivos: 13, temas: ['Sistemas de referencia anatómica', 'Aparatos y sistemas'] }
    ]
  },
  calculo: {
    id: 'calculo',
    nombre: 'Cálculo diferencial e integral',
    reactivos: 24,
    subareas: [
      { id: 'calc-dif', nombre: 'Cálculo diferencial', reactivos: 12, temas: ['Límites', 'La derivada', 'Aplicaciones'] },
      { id: 'calc-int', nombre: 'Cálculo integral', reactivos: 12, temas: ['La integral', 'Métodos de integración', 'Aplicaciones de la integral definida'] }
    ]
  },
  fisica: {
    id: 'fisica',
    nombre: 'Física',
    reactivos: 24,
    subareas: [
      { id: 'fis-mec', nombre: 'Mecánica', reactivos: null, temas: ['Sistemas de fuerzas', 'Aceleración y fuerza centrípeta', 'Trabajo y energía cinética rotacional', 'Momento angular', 'Impulso-cantidad de movimiento'] },
      { id: 'fis-optica', nombre: 'Óptica, ondas y electromagnetismo', reactivos: null, temas: ['Campos magnéticos', 'Fuerza magnética', 'Aplicaciones del electromagnetismo'] }
    ]
  },
  ciencias_experimentales: {
    id: 'ciencias_experimentales',
    nombre: 'Ciencias experimentales (general)',
    reactivos: 24,
    subareas: [
      { id: 'ce-quim', nombre: 'Química', reactivos: 8, temas: ['Estructura y cambios de la materia', 'Lenguaje químico inorgánico', 'Cuantificación en reacciones', 'Compuestos del carbono y macromoléculas'] },
      { id: 'ce-fis', nombre: 'Física', reactivos: 8, temas: ['Cinemática', 'Dinámica', 'Termología', 'Electricidad y magnetismo'] },
      { id: 'ce-bio', nombre: 'Biología', reactivos: 6, temas: ['Estructura y metabolismo celular'] }
    ]
  },
  premedicina: {
    id: 'premedicina',
    nombre: 'Premedicina',
    reactivos: 48,
    subareas: [
      { id: 'pre-salud', nombre: 'Salud pública y medicina comunitaria', reactivos: 10, temas: ['Concepto de salud y enfermedad', 'Triada ecológica', 'Cadena epidemiológica', 'Historia natural de la enfermedad', 'Periodos y niveles de prevención', 'Signos y síntomas'] },
      { id: 'pre-anatomia', nombre: 'Anatomía y fisiología', reactivos: 18, temas: ['Niveles de organización y divisiones anatómicas', 'Sistema nervioso', 'Endocrino y digestivo', 'Inmunológico y linfático', 'Tegumentario y músculo esquelético', 'Cardiovascular y respiratorio', 'Urinario y reproductor'] },
      { id: 'pre-bio', nombre: 'Biología celular y microbiología', reactivos: 10, temas: ['Organelos y funciones', 'Metabolismo y respiración celular', 'Genética y mecanismos de la herencia', 'Microbiología y parasitología médica'] },
      { id: 'pre-bioq', nombre: 'Bioquímica y biología molecular', reactivos: 10, temas: ['Transporte a través de membrana', 'Biomoléculas', 'Farmacología'] }
    ]
  }
};

// ---------------------------------------------------------------------
// 4. Asignación de módulos por carrera UNINAV
// ---------------------------------------------------------------------
export const ASIGNACIONES_UNINAV = {
  'medico-cirujano-naval': ['premedicina'], // 48, no combinable
  'enfermeria-naval': ['ciencias_salud', 'quimica'],
  'ing-sistemas-navales': ['calculo', 'fisica'],
  'ing-hidrografia': ['calculo', 'fisica'],
  'ing-aeronaval': ['calculo', 'fisica'], // validar convocatoria 2027
  // Técnico profesional (propuesta a validar contra convocatoria 2027)
  'adm-intendencia-naval': ['administracion', 'aritmetica'],
  'electronica-naval': ['fisica', 'aritmetica'],
  'informatica-naval': ['aritmetica', 'probabilidad_estadistica'],
  'electricidad-refrigeracion': ['fisica', 'aritmetica'],
  'combustion-propulsion': ['fisica', 'aritmetica'],
  'mecanica-aviacion-naval': ['fisica', 'aritmetica'],
};

// Carreras UNINAV (compatible con la app)
export const CARRERAS_UNINAV = [
  {
    id: 'medico-cirujano-naval',
    nombre: 'Médico Cirujano Naval',
    nivel: 'Profesional',
    modulosRequeridos: ['premedicina'],
    duracionSemestres: 10,
    sede: 'Escuela Médico Naval (CDMX)',
    descripcion: 'Formación médica militar integral. EXANI-II con Premedicina (48).'
  },
  {
    id: 'enfermeria-naval',
    nombre: 'Licenciatura en Enfermería Naval',
    nivel: 'Profesional',
    modulosRequeridos: ['ciencias_salud', 'quimica'],
    duracionSemestres: 8,
    sede: 'Escuela de Enfermería Naval (CDMX)',
    descripcion: 'Atención y cuidado del personal naval. Módulos: Ciencias de la Salud + Química.'
  },
  {
    id: 'ing-sistemas-navales',
    nombre: 'Ingeniería en Sistemas Navales',
    nivel: 'Profesional',
    modulosRequeridos: ['calculo', 'fisica'],
    duracionSemestres: 10,
    sede: 'Heroica Escuela Naval Militar (Antón Lizardo, Ver.)',
    descripcion: 'Mando táctico operativo de buques. Módulos: Cálculo + Física.'
  },
  {
    id: 'ing-hidrografia',
    nombre: 'Ingeniería en Hidrografía',
    nivel: 'Profesional',
    modulosRequeridos: ['calculo', 'fisica'],
    duracionSemestres: 10,
    sede: 'Heroica Escuela Naval Militar (Antón Lizardo, Ver.)',
    descripcion: 'Operaciones anfibias y cartografía hidrográfica. Módulos: Cálculo + Física.'
  },
  {
    id: 'ing-aeronaval',
    nombre: 'Ingeniería Aeronaval',
    nivel: 'Profesional',
    modulosRequeridos: ['calculo', 'fisica'],
    duracionSemestres: 10,
    sede: 'Heroica Escuela Naval Militar (Antón Lizardo, Ver.)',
    descripcion: 'Piloto aviador naval. Módulos: Cálculo + Física (validar 2027).'
  },
  {
    id: 'adm-intendencia-naval',
    nombre: 'Administración e Intendencia Naval',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['administracion', 'aritmetica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Administración + Aritmética (a validar).'
  },
  {
    id: 'electronica-naval',
    nombre: 'Electrónica Naval',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['fisica', 'aritmetica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Física + Aritmética (a validar).'
  },
  {
    id: 'informatica-naval',
    nombre: 'Informática Naval',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['aritmetica', 'probabilidad_estadistica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Aritmética + Probabilidad y estadística (a validar).'
  },
  {
    id: 'electricidad-refrigeracion',
    nombre: 'Electricidad y Refrigeración',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['fisica', 'aritmetica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Física + Aritmética (a validar).'
  },
  {
    id: 'combustion-propulsion',
    nombre: 'Combustión Interna y Sistemas de Propulsión',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['fisica', 'aritmetica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Física + Aritmética (a validar).'
  },
  {
    id: 'mecanica-aviacion-naval',
    nombre: 'Mecánica de Aviación Naval',
    nivel: 'Técnico Profesional',
    modulosRequeridos: ['fisica', 'aritmetica'],
    duracionSemestres: 6,
    sede: 'Escuela Naval de Mando y Estado Mayor',
    descripcion: 'Módulos propuestos: Física + Aritmética (a validar).'
  }
];

// ---------------------------------------------------------------------
// 5. Modalidades, habilidades socioemocionales y config del examen
// ---------------------------------------------------------------------
export const MODALIDADES_APLICACION = [
  { id: 'impresa', nombre: 'Examen Impreso', descripcion: 'Cuadernillo en papel con hoja de respuestas de lectura óptica.' },
  { id: 'en_linea', nombre: 'Examen En Línea', descripcion: 'Salas de cómputo institucionales con red local e internet.' },
  { id: 'desde_casa', nombre: 'Examen Desde Casa', descripcion: 'Domicilio del sustentante con equipo propio y navegador seguro.' }
];

export const HABILIDADES_SOCIOEMOCIONALES = [
  { id: 'gusto_escuela', nombre: 'Gusto por la escuela', descripcion: 'Disfrute intrínseco de las actividades escolares.' },
  { id: 'cooperacion', nombre: 'Cooperación', descripcion: 'Disposición al trabajo colaborativo para metas compartidas.' },
  { id: 'perseverancia', nombre: 'Perseverancia académica', descripcion: 'Determinación para concluir tareas ante obstáculos.' },
  { id: 'metacognicion', nombre: 'Metacognición', descripcion: 'Estrategias de autorregulación y monitoreo del aprendizaje.' },
  { id: 'compromiso', nombre: 'Compromiso académico', descripcion: 'Puntualidad, asistencia, dedicación y orden en el estudio.' }
];

export const EXAM_CONFIG = {
  TOTAL_REACTIVOS_COMPLETO: 168,
  TOTAL_REACTIVOS_TRANSVERSAL: 90,
  REACTIVOS_POR_MODULO: 24,
  REACTIVOS_PREMEDICINA: 48,
  DURACION_COMPLETO_MINUTOS: 270,
  DURACION_TRANSVERSAL_MINUTOS: 180,
  OPCIONES_POR_REACTIVO: 3,
  SISTEMA_PUNTUACION: {
    CORRECTO: 1,
    INCORRECTO: 0,
    PENALIZACION: false
  }
};

export const MODALIDADES_APLICACION_LIST = MODALIDADES_APLICACION.reduce((acc, m) => {
  acc[m.id] = m.nombre;
  return acc;
}, {});

// ---------------------------------------------------------------------
// 6. Taxonomía agregada (misma estructura del esquema del plan, Fase 1)
// ---------------------------------------------------------------------
export const EXANI_TAXONOMY = {
  version: EXANI_META.version,
  examen: EXANI_META.examen,
  nuevoModelo: EXANI_META.nuevoModelo,
  totalReactivos: EXANI_META.totalReactivos,
  puntuajeReactivos: EXANI_META.puntuajeReactivos,
  tiempoMinutos: EXANI_META.tiempoMinutos,
  escalaPuntaje: EXANI_META.escalaPuntaje,
  areasTransversales: [
    { id: 'pm', nombre: 'Pensamiento matemático', reactivos: 30, subareas: PENSAMIENTO_MATEMATICO_DETALLE.subareas },
    { id: 'cl', nombre: 'Comprensión lectora', reactivos: 30, subareas: COMPRENSION_LECTORA_DETALLE.subareas },
    { id: 'ri', nombre: 'Redacción indirecta', reactivos: 30, subareas: REDACION_INDIRECTA_DETALLE.subareas, dimensiones: REDACION_INDIRECTA_DETALLE.dimensiones },
    { id: 'in', nombre: 'Inglés como lengua extranjera (B1)', reactivos: 30, puntua: false, subareas: INGLES_DIAGNOSTICO_DETALLE.subareas }
  ],
  modulosDisciplinares: EXANI_MODULOS_ESPECIFICOS.map((m) => ({
    id: m.id,
    nombre: m.nombre,
    reactivos: m.reactivos,
    especifico: m.especifico,
    noCombinable: m.noCombinable || false,
    desglose: MODULOS_DETALLE[m.id] || { pendiente: true }
  })),
  modulosGenerales: EXANI_MODULOS_GENERALES.map((m) => ({
    id: m.id,
    nombre: m.nombre,
    reactivos: m.reactivos,
    desglose: MODULOS_DETALLE[m.id] || { pendiente: true }
  })),
  asignacionesUNINAV: ASIGNACIONES_UNINAV
};

// ---------------------------------------------------------------------
// 7. Validador de esquema (Fase 1 del plan)
// ---------------------------------------------------------------------
export function validateTaxonomy() {
  const errors = [];

  if (EXANI_META.totalReactivos !== 168) errors.push('totalReactivos debe ser 168');
  if (EXANI_META.puntuajeReactivos !== 138) errors.push('puntuajeReactivos debe ser 138');
  if (EXANI_META.opcionesPorReactivo !== 3) errors.push('opcionesPorReactivo debe ser 3');

  const transversalTotal = EXANI_AREAS_TRANSVERSALES
    .filter((a) => a.tipo === 'predictiva')
    .reduce((s, a) => s + a.reactivos, 0);
  if (transversalTotal !== 90) errors.push(`Áreas transversales predecibles deben sumar 90 (actual ${transversalTotal})`);

  for (const mod of EXANI_MODULOS_ESPECIFICOS) {
    const esperado = mod.noCombinable ? 48 : 24;
    if (mod.reactivos !== esperado) errors.push(`Módulo ${mod.nombre}: reactivos ${mod.reactivos} (esperado ${esperado})`);
  }

  // Validar que asignaciones refieran módulos existentes
  const modIds = new Set(EXANI_MODULOS_ESPECIFICOS.map((m) => m.id));
  for (const [carrera, modulos] of Object.entries(ASIGNACIONES_UNINAV)) {
    for (const m of modulos) {
      if (!modIds.has(m)) errors.push(`Carrera ${carrera}: módulo desconocido "${m}"`);
    }
    if (modulos.includes('premedicina') && modulos.length !== 1) {
      errors.push(`Carrera ${carrera}: Premedicina es no combinable (debe ser el único módulo)`);
    }
  }

  return { valid: errors.length === 0, errors };
}